import type { EventApi, EventInput } from '@fullcalendar/core';
import { CALENDAR_EVENT_TYPES } from '@/app/(main)/calendar/consts/calendar-event-types';
import { CALENDAR_TASK_STATUS } from '@/app/(main)/calendar/consts/calendar-task-status';
import { calendarTaskEventColors } from '@/app/(main)/calendar/utils/calendarTaskEventColors';
import { formatCalendarEventDisplayTitle } from '@/app/(main)/calendar/utils/formatCalendarEventDisplayTitle';
import { formatGroupedBirthdayLabel } from '@/app/(main)/calendar/utils/formatGroupedBirthdayLabel';
import { groupBirthdaysByDay } from '@/app/(main)/calendar/utils/groupBirthdaysByDay';
import { isPastUnfinishedCalendarTask } from '@/app/(main)/calendar/utils/isPastUnfinishedCalendarTask';
import type { CalendarEvent, CalendarVisibilityState } from '@/types/calendarTypes';

export const CALENDAR_EVENT_EXTENDED_PROP = 'calendarEvent' as const;
export const CALENDAR_GROUPED_BIRTHDAYS_PROP = 'groupedBirthdays' as const;

export type FullCalendarExtendedProps = {
  [CALENDAR_EVENT_EXTENDED_PROP]: CalendarEvent;
  [CALENDAR_GROUPED_BIRTHDAYS_PROP]?: CalendarEvent[];
};

export function getGroupedBirthdays(event: EventApi, fallback: CalendarEvent): CalendarEvent[] {
  const props = event.extendedProps as Partial<FullCalendarExtendedProps>;
  return props[CALENDAR_GROUPED_BIRTHDAYS_PROP] ?? [fallback];
}

function passesVisibility(event: CalendarEvent, visibility: CalendarVisibilityState): boolean {
  if (event.type === CALENDAR_EVENT_TYPES.BIRTHDAY && !visibility.birthdays) return false;
  if (
    (event.type === CALENDAR_EVENT_TYPES.TASK_DEADLINE || event.type === CALENDAR_EVENT_TYPES.TASK_REMINDER) &&
    !visibility.tasks
  ) {
    return false;
  }
  if (
    (event.type === CALENDAR_EVENT_TYPES.OUTLOOK_VIRTUAL || event.type === CALENDAR_EVENT_TYPES.OUTLOOK_IN_PERSON) &&
    !visibility.events
  ) {
    return false;
  }
  return true;
}

type EventColorSet = { backgroundColor: string; borderColor: string; textColor: string };

const OUTLOOK_VIRTUAL_COLORS = {
  backgroundColor: 'var(--color-blue-50)',
  borderColor: 'var(--color-blue-200)',
  textColor: 'var(--color-blue-900)',
} as const satisfies EventColorSet;

const OUTLOOK_IN_PERSON_COLORS = {
  backgroundColor: 'var(--color-blue-400)',
  borderColor: 'var(--color-blue-400)',
  textColor: 'var(--color-black)',
} as const satisfies EventColorSet;

const BIRTHDAY_COLORS = {
  backgroundColor: 'var(--color-green-100)',
  borderColor: 'transparent',
  textColor: 'var(--color-status-success)',
} as const satisfies EventColorSet;

const DEFAULT_EVENT_COLORS = {
  backgroundColor: 'var(--color-grey-100)',
  borderColor: 'var(--color-grey-400)',
  textColor: 'var(--color-black)',
} as const satisfies EventColorSet;

function eventColors(event: CalendarEvent): EventColorSet {
  switch (event.type) {
    case CALENDAR_EVENT_TYPES.OUTLOOK_VIRTUAL:
      return OUTLOOK_VIRTUAL_COLORS;
    case CALENDAR_EVENT_TYPES.OUTLOOK_IN_PERSON:
      return OUTLOOK_IN_PERSON_COLORS;
    case CALENDAR_EVENT_TYPES.BIRTHDAY:
      return BIRTHDAY_COLORS;
    case CALENDAR_EVENT_TYPES.TASK_REMINDER:
    case CALENDAR_EVENT_TYPES.TASK_DEADLINE:
      return calendarTaskEventColors(event);
    default:
      return DEFAULT_EVENT_COLORS;
  }
}

type BirthdayGroupInput = {
  id: string;
  birthdays: CalendarEvent[];
};

function mapSingleEventToFullCalendar(event: CalendarEvent, birthdayGroup?: BirthdayGroupInput): EventInput {
  const colors = eventColors(event);
  const isBirthday = event.type === CALENDAR_EVENT_TYPES.BIRTHDAY;
  const title = birthdayGroup
    ? formatGroupedBirthdayLabel(birthdayGroup.birthdays)
    : formatCalendarEventDisplayTitle(event);

  return {
    id: birthdayGroup && birthdayGroup.birthdays.length > 1 ? birthdayGroup.id : event.id,
    title,
    start: event.start,
    end: event.end,
    allDay: event.allDay ?? false,
    display: isBirthday ? 'block' : 'auto',
    editable: !isBirthday,
    backgroundColor: colors.backgroundColor,
    borderColor: colors.borderColor,
    textColor: colors.textColor,
    classNames: [
      'sf-calendar-event',
      `sf-calendar-event--${event.type}`,
      event.status === CALENDAR_TASK_STATUS.COMPLETED ? 'sf-calendar-event--completed' : '',
      event.status === CALENDAR_TASK_STATUS.CLOSED ? 'sf-calendar-event--closed' : '',
      isPastUnfinishedCalendarTask(event) ? 'sf-calendar-event--past-unfinished' : '',
    ].filter(Boolean),
    extendedProps: {
      [CALENDAR_EVENT_EXTENDED_PROP]: event,
      ...(birthdayGroup ? { [CALENDAR_GROUPED_BIRTHDAYS_PROP]: birthdayGroup.birthdays } : {}),
    } satisfies FullCalendarExtendedProps,
  };
}

export function mapCalendarEventsToFullCalendar(
  events: CalendarEvent[],
  visibility: CalendarVisibilityState
): EventInput[] {
  const visibleEvents = events.filter((e) => passesVisibility(e, visibility));
  const { nonBirthdayEvents, birthdayGroups } = groupBirthdaysByDay(visibleEvents);

  return [
    ...nonBirthdayEvents.map((event) => mapSingleEventToFullCalendar(event)),
    ...birthdayGroups.map(({ dateKey, birthdays }) =>
      mapSingleEventToFullCalendar(birthdays[0], { id: `birthday-group-${dateKey}`, birthdays })
    ),
  ];
}
